import React, { useEffect, useState, useContext } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import { AuthContext } from '../../../context/AuthContext';
import { FaTrashAlt, FaImage, FaSearch, FaCalendarAlt, FaUser } from 'react-icons/fa';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const AdminGallery = () => {
  const { authToken } = useContext(AuthContext);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const fetchPhotos = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/gallery`);
      if (!response.ok) throw new Error('Error al cargar fotos de la galería.');
      const data = await response.json();
      setPhotos(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPhotos();
  }, []);

  const handleDelete = async (photoId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta foto de la galería?')) return;
    setDeletingId(photoId);
    try {
      const response = await fetch(`${API_BASE_URL}/gallery/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'No se pudo eliminar la foto.');
      }
      setPhotos(photos.filter(p => p.id !== photoId));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const filteredPhotos = photos.filter(photo => {
    return (
      (photo.username || '').toLowerCase().includes(search.toLowerCase()) ||
      (photo.comment || '').toLowerCase().includes(search.toLowerCase()) ||
      (photo.discord_id || '').includes(search)
    );
  });

  return (
    <div className="admin-users-container">
      <AdminSidebar />
      <div className="admin-content">
        <h1 className="admin-page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <FaImage style={{ color: '#ffd700' }} /> Moderación de Galería
        </h1>

        {/* Buscador */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '280px' }}>
            <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)' }} />
            <input
              type="text"
              placeholder="Buscar por usuario, comentario o Discord ID..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 15px 10px 38px',
                borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.05)',
                color: '#fff',
                fontSize: '0.95rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <button 
            onClick={fetchPhotos} 
            style={{
              padding: '10px 20px',
              borderRadius: '8px',
              border: '1px solid #ffd700',
              background: 'transparent',
              color: '#ffd700',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Actualizar
          </button>
        </div>

        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '40px 0' }}>
            <div className="modern-spinner" />
            <span style={{ color: '#888', fontWeight: 500, marginTop: 12 }}>Cargando fotos de la galería...</span>
          </div>
        )}

        {error && <p className="error-message">{error}</p>}

        {!loading && !error && filteredPhotos.length === 0 && (
          <div style={{ textAlign: 'center', color: '#888', padding: '40px 0', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
            No se encontraron fotos publicadas.
          </div>
        )}

        {!loading && !error && filteredPhotos.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '20px',
            marginTop: '10px'
          }}>
            {filteredPhotos.map(photo => {
              const fileUrl = photo.file_path.startsWith('/api') 
                ? `${API_BASE_URL.replace('/api', '')}${photo.file_path}` 
                : `${API_BASE_URL}${photo.file_path}`;

              return (
                <div key={photo.id} style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative'
                }}>
                  {/* Botón de Borrado */}
                  <button
                    onClick={() => handleDelete(photo.id)}
                    disabled={deletingId === photo.id}
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      background: 'rgba(219, 68, 85, 0.9)',
                      border: 'none',
                      borderRadius: '50%',
                      width: '36px',
                      height: '36px',
                      color: 'white',
                      cursor: deletingId === photo.id ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                      transition: 'all 0.2s',
                      zIndex: '5'
                    }}
                    title="Eliminar Foto"
                  >
                    <FaTrashAlt size={14} />
                  </button>

                  {/* Imagen */}
                  <div style={{
                    width: '100%',
                    height: '180px',
                    overflow: 'hidden',
                    background: '#000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderBottom: '1px solid rgba(255,255,255,0.05)'
                  }}>
                    <img
                      src={fileUrl}
                      alt={photo.comment || 'Foto de la Galería'}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1542751371-adc38448a05e?q=80&w=600';
                      }}
                    />
                  </div>

                  {/* Detalles */}
                  <div style={{ padding: '15px', display: 'flex', flexDirection: 'column', flex: '1' }}>
                    <p style={{
                      color: '#ddd',
                      fontSize: '0.92rem',
                      margin: '0 0 15px 0',
                      lineHeight: '1.4',
                      fontStyle: photo.comment ? 'normal' : 'italic',
                      flex: '1'
                    }}>
                      {photo.comment || 'Sin descripción.'}
                    </p>

                    <div style={{
                      borderTop: '1px solid rgba(255,255,255,0.05)',
                      paddingTop: '12px',
                      marginTop: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      fontSize: '0.8rem',
                      color: '#888'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FaUser size={12} style={{ color: '#ffd700' }} />
                        <span>Por: <strong>{photo.username}</strong> ({photo.discord_id})</span>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <FaCalendarAlt size={12} />
                        <span>Fecha: {new Date(photo.created_at).toLocaleString('es-ES')}</span>
                      </span>
                      <span style={{ fontSize: '0.75rem', color: '#555', marginTop: '4px' }}>ID de Foto: {photo.id}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGallery;
